// LogsDashboard.jsx
import React, { useState, useEffect } from "react";
import {
  getLogs,
  getLogsStats,
  exportLogs,
  getWordCloudData,
} from "../services/api";
import ReactWordcloud from "react-wordcloud";

import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
} from "chart.js";
import "chartjs-adapter-date-fns";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale
);

// Custom hook to track screen size
const useScreenSize = () => {
  const [screenSize, setScreenSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  useEffect(() => {
    const handleResize = () => {
      setScreenSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return screenSize;
};

const LogsDashboard = () => {
  const { width: screenWidth } = useScreenSize();
  const isSmallScreen = screenWidth < 1200; // Breakpoint for stacked layout

  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [wordCloudData, setWordCloudData] = useState([]);
  const [wordCloudLoading, setWordCloudLoading] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    language: "",
    search: "",
    serial_number: "",
  });

  useEffect(() => {
    fetchWordCloud();
  }, [filters]);

  // Chart settings
  const [timeGrouping, setTimeGrouping] = useState("day");

  useEffect(() => {
    fetchLogs();
  }, [currentPage, filters]);

  useEffect(() => {
    fetchStats();
  }, [timeGrouping]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        language: filters.language,
        search: filters.search,
        serial_number: filters.serial_number,
      };

      const response = await getLogs(currentPage, 20, params);
      setLogs(response.data.logs);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await getLogsStats(timeGrouping, 30);
      setStats(response.data.stats);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setStatsLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      language: "",
      search: "",
      serial_number: "",
    });
    setCurrentPage(1);
  };

  const handleExport = async () => {
    try {
      const response = await exportLogs(filters);
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `logs_export_${new Date().toISOString().split("T")[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const fetchWordCloud = async () => {
    setWordCloudLoading(true);
    try {
      const params = {
        start_date: filters.start_date,
        end_date: filters.end_date,
        language: filters.language,
      };

      const response = await getWordCloudData(params);
      setWordCloudData(response.data.words);
    } catch (error) {
      console.error("Error fetching word cloud:", error);
    } finally {
      setWordCloudLoading(false);
    }
  };

  // Word cloud options
  const wordCloudOptions = {
    colors: ["#00205B", "#1E2A63", "#3A4A7C", "#566A95", "#728AAE"],
    enableTooltip: true,
    deterministic: false,
    fontFamily: "Roboto",
    fontSizes: [12, 60],
    fontStyle: "normal",
    fontWeight: "normal",
    padding: 3,
    rotations: 2,
    rotationAngles: [0, 90],
    scale: "sqrt",
    spiral: "archimedean",
    transitionDuration: 1000,
  };

  // Word cloud callbacks
  const wordCloudCallbacks = {
    onWordClick: (word) => {
      console.log("Clicked word:", word);
      if (word.text !== "<VIN>") {
        handleFilterChange("search", word.text);
      }
    },
    onWordMouseOver: (word, event) => {
      console.log("Hovered word:", word);
    },
  };

  // Chart data
  const chartData = {
    datasets: [
      {
        label: "Requests",
        data: stats.map((stat) => ({
          x: new Date(stat.period),
          y: stat.count,
        })),
        borderColor: "#00205B",
        backgroundColor: "rgba(0, 32, 91, 0.1)",
        tension: 0.1,
        fill: false,
        pointRadius: 2,
        pointHoverRadius: 4,
      },
    ],
  };

  // Chart options
  const getChartOptions = () => {
    let timeUnit, tooltipFormat, displayFormat;

    switch (timeGrouping) {
      case "minute":
        timeUnit = "minute";
        tooltipFormat = "MMM dd, HH:mm";
        displayFormat = "HH:mm";
        break;
      case "hour":
        timeUnit = "hour";
        tooltipFormat = "MMM dd, HH:mm";
        displayFormat = "HH:mm";
        break;
      default:
        timeUnit = "day";
        tooltipFormat = "MMM dd, yyyy";
        displayFormat = "MMM dd";
        break;
    }

    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: "top",
        },
        title: {
          display: true,
          text: `Requests per ${timeGrouping}`,
          font: {
            size: 16,
            weight: "bold",
          },
          color: "#00205B",
        },
        tooltip: {
          mode: "index",
          intersect: false,
          callbacks: {
            title: function (tooltipItems) {
              const date = new Date(tooltipItems[0].parsed.x);
              return (
                date.toLocaleDateString() + " " + date.toLocaleTimeString()
              );
            },
          },
        },
      },
      scales: {
        x: {
          type: "time",
          time: {
            unit: timeUnit,
            displayFormats: {
              [timeUnit]: displayFormat,
            },
            tooltipFormat: tooltipFormat,
          },
          title: {
            display: true,
            text:
              timeGrouping === "minute"
                ? "Time (Minutes)"
                : timeGrouping === "hour"
                ? "Time (Hours)"
                : "Date",
            color: "#00205B",
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: "Number of Requests",
            color: "#00205B",
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
          ticks: {
            stepSize: 1,
            callback: function (value) {
              return Number.isInteger(value) ? value : "";
            },
          },
        },
      },
      interaction: {
        mode: "nearest",
        axis: "x",
        intersect: false,
      },
      elements: {
        point: {
          radius: 3,
          hoverRadius: 6,
        },
        line: {
          borderWidth: 2,
        },
      },
    };
  };

  // Common panel styles
  const getPanelStyles = () => {
    if (isSmallScreen) {
      return {
        width: "100%",
        marginBottom: 15,
      };
    }
    return {};
  };

  return (
    <div
      style={{
        width: isSmallScreen ? "100%" : 1800,
        minWidth: isSmallScreen ? 800 : 1800,
        height: isSmallScreen ? "auto" : 800,
        minHeight: isSmallScreen ? 800 : 800,
        position: "relative",
        background: "white",
        overflow: isSmallScreen ? "auto" : "hidden",
      }}
    >
      <div
        style={{
          width: isSmallScreen ? "calc(100% - 40px)" : 1700,
          height: isSmallScreen ? "auto" : 723,
          left: 20,
          top: 30,
          position: isSmallScreen ? "relative" : "absolute",
          backgroundRepeat: "no-repeat",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "flex-start",
          gap: 10,
          display: "inline-flex",
        }}
      >
        {/* Header */}
        <div
          style={{
            width: "100%",
            height: 60,
            paddingLeft: 20,
            paddingRight: 20,
            background: "white",
            justifyContent: "space-between",
            alignItems: "center",
            display: "flex",
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          <h1
            style={{
              color: "#00205B",
              fontSize: 24,
              fontFamily: "Roboto",
              fontWeight: "600",
              margin: 0,
            }}
          >
            Logs Dashboard
          </h1>
          <div
            style={{
              display: "flex",
              gap: 10,
              alignItems: "center",
            }}
          >
            <span style={{ color: "#666", fontSize: 14 }}>
              Total: {total} logs
            </span>
            <span style={{ color: "#999", fontSize: 12 }}>
              ({isSmallScreen ? "Stacked" : "Side-by-side"})
            </span>
          </div>
        </div>

        {/* Main Content - Responsive layout */}
        <div
          style={{
            flex: 1,
            width: "100%",
            padding: 20,
            display: "flex",
            flexDirection: isSmallScreen ? "column" : "row",
            gap: 20,
            overflow: isSmallScreen ? "visible" : "hidden",
          }}
        >
          {/* Chart Panel */}
          <div
            style={{
              width: isSmallScreen ? "100%" : "29%",
              display: "flex",
              flexDirection: "column",
              gap: 15,
              ...getPanelStyles(),
            }}
          >
            {/* Chart Controls */}
            <div
              style={{
                background: "white",
                padding: 15,
                borderRadius: 5,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: "#00205B",
                  }}
                >
                  Time Period:
                </span>
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {[
                  { key: "day", label: "Last 30 Days" },
                  { key: "hour", label: "Last 24 Hours" },
                  { key: "minute", label: "Last Hour" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    onClick={() => setTimeGrouping(key)}
                    style={{
                      padding: "8px 16px",
                      background: timeGrouping === key ? "#00205B" : "white",
                      color: timeGrouping === key ? "white" : "#00205B",
                      border: `1px solid #00205B`,
                      borderRadius: 3,
                      cursor: "pointer",
                      fontSize: 12,
                      fontWeight: "600",
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Chart */}
            <div
              style={{
                background: "white",
                padding: 15,
                borderRadius: 5,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                flex: isSmallScreen ? "none" : 1,
                minHeight: isSmallScreen ? 350 : 400,
                height: isSmallScreen ? 350 : "auto",
              }}
            >
              {statsLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#666",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                  }}
                >
                  Loading chart...
                </div>
              ) : (
                <div style={{ height: "100%", width: "100%" }}>
                  <div
                    style={{ fontSize: 12, color: "#666", marginBottom: 10 }}
                  >
                    Data points: {stats.length} | Non-zero:{" "}
                    {stats.filter((s) => s.count > 0).length}
                  </div>
                  <Line data={chartData} options={getChartOptions()} />
                </div>
              )}
            </div>
          </div>

          {/* Table Panel */}
          <div
            style={{
              width: isSmallScreen ? "100%" : "42%",
              display: "flex",
              flexDirection: "column",
              gap: 15,
              ...getPanelStyles(),
            }}
          >
            {/* Filters */}
            <div
              style={{
                background: "white",
                padding: 15,
                borderRadius: 5,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
              }}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isSmallScreen
                    ? "repeat(2, 1fr)"
                    : "repeat(4, 1fr)",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <input
                  type="date"
                  placeholder="Start Date"
                  value={filters.start_date}
                  onChange={(e) =>
                    handleFilterChange("start_date", e.target.value)
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                />
                <input
                  type="date"
                  placeholder="End Date"
                  value={filters.end_date}
                  onChange={(e) =>
                    handleFilterChange("end_date", e.target.value)
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                />
                <select
                  value={filters.language}
                  onChange={(e) =>
                    handleFilterChange("language", e.target.value)
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                >
                  <option value="">All Languages</option>
                  <option value="gb">English</option>
                  <option value="de">German</option>
                  <option value="fr">French</option>
                  <option value="es">Spanish</option>
                  <option value="pt">Portuguese</option>
                  <option value="nl">Dutch</option>
                  <option value="pl">Polish</option>
                </select>
                <input
                  type="text"
                  placeholder="Search queries..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                />
                <input
                  type="text"
                  placeholder="Search SerialNumber..."
                  value={filters.serial_number}
                  onChange={(e) =>
                    handleFilterChange("serial_number", e.target.value)
                  }
                  style={{
                    padding: 8,
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    fontSize: 12,
                  }}
                />
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={clearFilters}
                  style={{
                    padding: "8px 16px",
                    background: "white",
                    color: "#666",
                    border: "1px solid #ddd",
                    borderRadius: 3,
                    cursor: "pointer",
                    fontSize: 12,
                  }}
                >
                  Clear Filters
                </button>
                <button
                  onClick={handleExport}
                  style={{
                    padding: "8px 16px",
                    background: "#00205B",
                    color: "white",
                    border: "none",
                    borderRadius: 3,
                    cursor: "pointer",
                    fontSize: 12,
                    fontWeight: "600",
                  }}
                >
                  Download CSV
                </button>
              </div>
            </div>

            {/* Table */}
            <div
              style={{
                background: "white",
                borderRadius: 5,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                flex: isSmallScreen ? "none" : 1,
                display: "flex",
                flexDirection: "column",
                minHeight: isSmallScreen ? 400 : "auto",
                height: isSmallScreen ? 400 : "auto",
              }}
            >
              <div
                style={{
                  padding: 15,
                  borderBottom: "1px solid #e0e0e0",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: "600",
                    color: "#00205B",
                  }}
                >
                  Logs ({total} total)
                </span>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                    disabled={currentPage === 1}
                    style={{
                      padding: "4px 8px",
                      background: currentPage === 1 ? "#f5f5f5" : "#00205B",
                      color: currentPage === 1 ? "#999" : "white",
                      border: "none",
                      borderRadius: 3,
                      cursor: currentPage === 1 ? "not-allowed" : "pointer",
                      fontSize: 12,
                    }}
                  >
                    ‹
                  </button>
                  <span style={{ fontSize: 12, color: "#666" }}>
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPage + 1))
                    }
                    disabled={currentPage === totalPages}
                    style={{
                      padding: "4px 8px",
                      background:
                        currentPage === totalPages ? "#f5f5f5" : "#00205B",
                      color: currentPage === totalPages ? "#999" : "white",
                      border: "none",
                      borderRadius: 3,
                      cursor:
                        currentPage === totalPages ? "not-allowed" : "pointer",
                      fontSize: 12,
                    }}
                  >
                    ›
                  </button>
                </div>
              </div>

              <div style={{ flex: 1, overflow: "auto" }}>
                {loading ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: 20,
                      color: "#666",
                    }}
                  >
                    Loading logs...
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#f8f9fa" }}>
                        <th
                          style={{
                            padding: 10,
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 12,
                            fontWeight: "600",
                            color: "#00205B",
                          }}
                        >
                          ID
                        </th>
                        <th
                          style={{
                            padding: 10,
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 12,
                            fontWeight: "600",
                            color: "#00205B",
                          }}
                        >
                          Query
                        </th>
                        <th
                          style={{
                            padding: 10,
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 12,
                            fontWeight: "600",
                            color: "#00205B",
                          }}
                        >
                          Timestamp
                        </th>
                        <th
                          style={{
                            padding: 10,
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 12,
                            fontWeight: "600",
                            color: "#00205B",
                          }}
                        >
                          Language
                        </th>
                        <th
                          style={{
                            padding: 10,
                            textAlign: "left",
                            borderBottom: "1px solid #e0e0e0",
                            fontSize: 12,
                            fontWeight: "600",
                            color: "#00205B",
                          }}
                        >
                          SerialNumber
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr
                          key={log.id}
                          style={{
                            borderBottom: "1px solid #f0f0f0",
                          }}
                        >
                          <td
                            style={{
                              padding: 10,
                              fontSize: 12,
                              color: "#666",
                            }}
                          >
                            {log.id}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              fontSize: 12,
                              color: "#333",
                              maxWidth: isSmallScreen ? 300 : 200,
                              wordBreak: "break-word",
                            }}
                          >
                            {log.query}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              fontSize: 12,
                              color: "#666",
                            }}
                          >
                            {formatDate(log.timestamp)}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              fontSize: 12,
                              color: "#666",
                            }}
                          >
                            {log.language.toUpperCase()}
                          </td>
                          <td
                            style={{
                              padding: 10,
                              fontSize: 12,
                              color: "#333",
                              maxWidth: isSmallScreen ? 300 : 200,
                              wordBreak: "break-word",
                            }}
                          >
                            {log.serial_number}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>

          {/* Word Cloud Panel */}
          <div
            style={{
              width: isSmallScreen ? "100%" : "29%",
              display: "flex",
              flexDirection: "column",
              gap: 15,
              ...getPanelStyles(),
            }}
          >
            <div
              style={{
                background: "white",
                padding: 15,
                borderRadius: 5,
                boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                flex: isSmallScreen ? "none" : 1,
                minHeight: isSmallScreen ? 350 : 400,
                height: isSmallScreen ? 350 : "auto",
              }}
            >
              <div style={{ marginBottom: 15 }}>
                <h3
                  style={{
                    margin: 0,
                    fontSize: 16,
                    fontWeight: "600",
                    color: "#00205B",
                  }}
                >
                  Frequent Queries
                </h3>
                <p
                  style={{
                    margin: "5px 0 0 0",
                    fontSize: 12,
                    color: "#666",
                  }}
                >
                  Click words to filter table
                </p>
              </div>

              {wordCloudLoading ? (
                <div
                  style={{
                    textAlign: "center",
                    color: "#666",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "80%",
                  }}
                >
                  Loading word cloud...
                </div>
              ) : wordCloudData.length > 0 ? (
                <div style={{ height: "calc(100% - 60px)", width: "100%" }}>
                  <ReactWordcloud
                    words={wordCloudData}
                    options={wordCloudOptions}
                    callbacks={wordCloudCallbacks}
                  />
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    color: "#999",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "80%",
                    fontStyle: "italic",
                  }}
                >
                  No queries to display
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LogsDashboard;
